import { Box, Button, Tooltip, Typography } from '@mui/material';
import RequestDocumentDialog from 'components/common/Requests/RequestDocumentDialog';
import RequestQuoteOrSampleDialog from 'components/common/Requests/RequestQuoteOrSampleDialog';
import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { ContactRequestContext, RequestTypes } from 'types/ContactRequests';
import { EventProduct, ProductType } from 'types/Products';
import { OutOfStockSection } from '../OutOfStockSection';
import RequestContactDialog from 'components/common/Requests/RequestContactDialog';
import { useIfVerifiedUser } from 'hooks/useUserState_utils';
import { LightTooltip, NeedMoreTooltip } from './LightToolTip';
import { isProductOutOfStock } from 'components/pages/products/helpers/formAttributes';
import { UXB_EVENT_KEY } from 'supplier-portals/uxbeauty/constants';
import { LogEventType } from 'types/LogEvents';
import { useLogEvent } from 'api/services/LogEventService';
import { WHITELABEL_EVENT_KEY } from 'components/constants/Constants';
import { generateSearchFilters } from 'helpers/utilsTS';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';
import { AddToCartDialog } from 'components/common/Requests/AddToCartDialog';
import { useCartState } from 'supplier-portals/uxbeauty/contexts/cart/CartContext';

export const RequestsUXB = ({ product }: { product: EventProduct }) => {
  return (
    <Box>
      <Typography
        sx={{
          color: 'GrayText',
          marginBottom: 2
        }}
      >
        Requests
      </Typography>
      <UXBRequests product={product} />
    </Box>
  );
};

const UXBRequests = ({ product }: { product: EventProduct }) => {
  type RequestModalType =
    | 'sample'
    | 'quote'
    | 'contact'
    | 'requestDocument'
    | 'addToCart';
  const [requestModals, setRequestModals] = useState<
    RequestModalType | undefined
  >(undefined);
  const verified = useIfVerifiedUser();
  const logEvent = useLogEvent();
  const { isAuthenticated } = useAuth();
  const disabled = !isAuthenticated || !verified;
  const { value } = useCartState();
  const isProductOnCart = value.items.some(
    item => item.old_key === product.old_key
  );

  const isOutOfStock = isProductOutOfStock(
    product.event.product_private_attributes?.data?.inStock?.[0]
  );
  const handleRequestModal = (
    type: RequestModalType,
    eventLog: LogEventType | null
  ) => {
    eventLog &&
      logEvent(
        [
          {
            productId: product.old_key,
            segment: product.segment,
            supplierId: product.company.key,
            productName: product.name,
            //has to be sent otherwise Be will throw an 500 error
            searchParameters: {
              position: 0,
              searchQuery: '',
              searchFilter: generateSearchFilters([], product.segment)
            }
          }
        ],
        eventLog,
        WHITELABEL_EVENT_KEY
      );

    setRequestModals(type);
  };

  const SamplesButton = () => {
    return (
      <div style={{ display: isOutOfStock ? 'none' : 'block' }}>
        <Button
          variant="outlined"
          disabled={disabled || isProductOnCart}
          onClick={() =>
            handleRequestModal('addToCart', LogEventType.OPENED_REQUEST_SAMPLE)
          }
          sx={{ gap: 1 }}
        >
          <ShoppingCartOutlined /> Samples
        </Button>
      </div>
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2
        }}
      >
        {isAuthenticated ? (
          <Tooltip
            title={isProductOnCart ? 'Product already added to cart' : null}
          >
            <div>
              <SamplesButton />
            </div>
          </Tooltip>
        ) : (
          <LightTooltip title={disabled ? <NeedMoreTooltip /> : null}>
            <div>
              <SamplesButton />
            </div>
          </LightTooltip>
        )}
        <LightTooltip title={disabled ? <NeedMoreTooltip /> : null}>
          <div>
            <Button
              variant="outlined"
              disabled={disabled}
              onClick={() =>
                handleRequestModal(
                  'requestDocument',
                  LogEventType.OPENED_REQUEST_DOCUMENT
                )
              }
            >
              Documents
            </Button>
          </div>
        </LightTooltip>

        {product.type === ProductType.INGREDIENTS && (
          <LightTooltip title={disabled ? <NeedMoreTooltip /> : null}>
            <div>
              <Button
                variant="outlined"
                disabled={disabled}
                onClick={() =>
                  handleRequestModal('quote', LogEventType.OPENED_REQUEST_QUOTE)
                }
              >
                Quote
              </Button>
            </div>
          </LightTooltip>
        )}
        <LightTooltip title={disabled ? <NeedMoreTooltip /> : null}>
          <div>
            <Button
              variant="outlined"
              disabled={disabled}
              onClick={() =>
                handleRequestModal(
                  'contact',
                  LogEventType.OPENED_REQUEST_CONTACT
                )
              }
            >
              Contact
            </Button>
          </div>
        </LightTooltip>
      </Box>
      {isOutOfStock && <OutOfStockSection />}
      {requestModals === 'sample' && product.event.key && (
        <RequestQuoteOrSampleDialog
          productType={product.type as ProductType}
          show={requestModals === 'sample'}
          setModal={handleRequestModal}
          context={{
            type: ContactRequestContext.SUPPLIER_PORTAL,
            portalKey: UXB_EVENT_KEY
          }}
          requestType={RequestTypes.SAMPLE}
          fullProducts={[product]}
        />
      )}
      {requestModals === 'requestDocument' && product.event.key && (
        <RequestDocumentDialog
          productType={product.type as ProductType}
          show={requestModals === 'requestDocument'}
          setModal={handleRequestModal}
          context={{
            type: ContactRequestContext.SUPPLIER_PORTAL,
            portalKey: UXB_EVENT_KEY
          }}
          requestType={RequestTypes.DOCUMENT}
          fullProducts={[product]}
        />
      )}
      {requestModals === 'quote' &&
        product.event.key &&
        product.type === ProductType.INGREDIENTS && (
          <RequestQuoteOrSampleDialog
            productType={product.type as ProductType}
            show={requestModals === 'quote'}
            setModal={handleRequestModal}
            context={{
              type: ContactRequestContext.SUPPLIER_PORTAL,
              portalKey: UXB_EVENT_KEY
            }}
            requestType={RequestTypes.QUOTE}
            fullProducts={[product]}
          />
        )}
      {requestModals === 'contact' && product.event.key && (
        <RequestContactDialog
          productType={product.type as ProductType}
          show={requestModals === 'contact'}
          setModal={handleRequestModal}
          context={{
            type: ContactRequestContext.SUPPLIER_PORTAL,
            portalKey: UXB_EVENT_KEY
          }}
          requestType={RequestTypes.CONTACT}
          fullProducts={[product]}
        />
      )}
      {requestModals === 'addToCart' && product.event.key && (
        <AddToCartDialog
          open={requestModals === 'addToCart'}
          onClose={() => setRequestModals(undefined)}
          products={[product]}
        />
      )}
    </Box>
  );
};
